import { Fragment, type ReactNode } from 'react';
import { lawLink } from '../../dashboard/src/lawLink';
import { withBrand } from './Brand';

/**
 * Law references in visitor copy, each linked to the law itself on EUR-Lex.
 * Copper on this page means "clickable" (Leo, 2026-09-26): a term with no
 * source to open (ComplyEdge, GPAI, runtime enforcement) stays plain text.
 * Values are citations in the rule format lawLink resolves; nothing here
 * builds a URL by hand. Longest match first. The brand name in plain text is
 * bold, as in every other piece of copy (Leo, 2026-09-28; Brand.tsx).
 */
const EU_AI_ACT = 'Regulation (EU) 2024/1689';

export const TRUST_LAW_TERMS: Record<string, string> = {
  'EU AI Act': EU_AI_ACT,
  'Article 50': `${EU_AI_ACT}, Article 50`,
  'Article 5': `${EU_AI_ACT}, Article 5`,
};

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Word boundaries prevent "Article 5" from matching inside "Article 50" / "51".
const LAW_TERM_PATTERN = new RegExp(
  `\\b(${Object.keys(TRUST_LAW_TERMS)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegex)
    .join('|')})\\b`,
  'gi',
);

export interface TrustTermPart {
  text: string;
  /** EUR-Lex URL when this part names a law; absent for plain text. */
  href?: string;
}

function hrefFor(term: string): string | undefined {
  const key = Object.keys(TRUST_LAW_TERMS).find((k) => k.toLowerCase() === term.toLowerCase());
  return key ? (lawLink(TRUST_LAW_TERMS[key], key).href ?? undefined) : undefined;
}

export function splitTrustKeyTerms(text: string): TrustTermPart[] {
  const parts: TrustTermPart[] = [];
  let last = 0;

  for (const match of text.matchAll(LAW_TERM_PATTERN)) {
    const index = match.index ?? 0;
    if (index > last) {
      parts.push({ text: text.slice(last, index) });
    }
    parts.push({ text: match[0], href: hrefFor(match[0]) });
    last = index + match[0].length;
  }

  if (last < text.length) {
    parts.push({ text: text.slice(last) });
  }

  return mergeAdjacentLinks(parts.length > 0 ? parts : [{ text }]);
}

/**
 * Two links separated only by whitespace read as one phrase split in two
 * ("EU AI Act" | "Article 5"), so they become ONE link to the more specific
 * target, the later part (Leo, 2026-09-26: never two links side by side).
 */
function mergeAdjacentLinks(parts: TrustTermPart[]): TrustTermPart[] {
  const out: TrustTermPart[] = [];
  for (const part of parts) {
    const prev = out[out.length - 1];
    const gap = out[out.length - 2];
    if (part.href && prev && !prev.href && /^\s+$/.test(prev.text) && gap?.href) {
      out.splice(out.length - 2, 2, {
        text: `${gap.text}${prev.text}${part.text}`,
        href: part.href,
      });
      continue;
    }
    if (part.href && prev?.href) {
      out[out.length - 1] = { text: `${prev.text}${part.text}`, href: part.href };
      continue;
    }
    out.push(part);
  }
  return out;
}

/** Law references become EUR-Lex links in CE coral, underlined at rest so color is
 *  not the only cue (app.css .trust-law-link); the rest is plain, brand bold. */
export function highlightKeyTermsInText(text: string): ReactNode[] {
  return splitTrustKeyTerms(text).map((part, index) =>
    part.href ? (
      <a
        key={index}
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
        className="trust-law-link"
      >
        {part.text}
      </a>
    ) : (
      <Fragment key={index}>{withBrand(part.text)}</Fragment>
    ),
  );
}

export function TrustKeyTerms({ children }: { children: string }) {
  const parts = splitTrustKeyTerms(children);
  if (parts.length === 1 && !parts[0].href && withBrand(parts[0].text) === parts[0].text) {
    return <>{parts[0].text}</>;
  }
  const nodes = highlightKeyTermsInText(children);
  return (
    <>
      {nodes.map((node, index) => (
        <Fragment key={index}>{node}</Fragment>
      ))}
    </>
  );
}
